import { computed, Ref } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { maxLength, helpers } from '@vuelidate/validators'

interface PriceRow {
  label: string
  price: string
}

interface WefoData {
  tile_title: string
  title1: string
  title2: string
  description: string
  location_text: string
  walking_time: number | null
  index_enabled: boolean
  index_titles: string[]
  free_admission: boolean
  prices: PriceRow[]
  price_level: string
}

const msg = (message: string, validator: any) => helpers.withMessage(message, validator)

const requiredMsg = (message: string) =>
  helpers.withMessage(message, (value: any) => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim().length > 0
    return true
  })

// Optional numeric validators – skip when value is null/undefined
const optionalInteger = helpers.withMessage(
  'Fußweg muss eine ganze Zahl sein.',
  (val: number | null) => val === null || val === undefined || Number.isInteger(Number(val))
)

const optionalBetween = helpers.withMessage(
  'Fußweg muss zwischen 1 und 999 Minuten liegen.',
  (val: number | null) => val === null || val === undefined || (Number(val) >= 1 && Number(val) <= 999)
)

export function useLocationWefoValidation(data: Ref<WefoData>, enabled: Ref<boolean>) {
  const rules = computed(() => {
    if (!enabled.value) return {}

    return {
      tile_title: {
        maxLength: msg('Kachel-Titel darf max. 255 Zeichen lang sein.', maxLength(255)),
      },
      title1: {
        maxLength: msg('Titel 1 darf max. 255 Zeichen lang sein.', maxLength(255)),
      },
      title2: {
        maxLength: msg('Titel 2 darf max. 255 Zeichen lang sein.', maxLength(255)),
      },
      description: {
        maxLength: msg('Beschreibung darf max. 5000 Zeichen lang sein.', maxLength(5000)),
      },
      location_text: {
        maxLength: msg('Standort-Text darf max. 255 Zeichen lang sein.', maxLength(255)),
      },
      walking_time: {
        optionalInteger,
        optionalBetween,
      },
      prices: {
        $each: helpers.forEach({
          label: {
            required: requiredMsg('Bezeichnung ist ein Pflichtfeld.'),
            maxLength: msg('Bezeichnung darf max. 255 Zeichen lang sein.', maxLength(255)),
          },
          price: {
            required: requiredMsg('Preis ist ein Pflichtfeld.'),
            maxLength: msg('Preis darf max. 255 Zeichen lang sein.', maxLength(255)),
          },
        }),
      },
      index_titles: {
        $each: helpers.forEach({
          $model: {
            maxLength: msg('Index-Titel darf max. 255 Zeichen lang sein.', maxLength(255)),
          },
        }),
      },
    }
  })

  const v$ = useVuelidate(rules, data, { $lazy: true })

  return { v$ }
}
